import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { checkInByScan } from "../../hooks/useRegistration";
import { useRoles } from "../../hooks/useRoles";
import { EventRegistration } from "../../types/shared.types";
import "./CheckInPage.css";

type CheckInState =
  | { phase: "loading" }
  | { phase: "success"; registration: EventRegistration }
  | { phase: "already"; registration: EventRegistration }
  | { phase: "no-permission" }
  | { phase: "error"; message: string };

const CheckInPage: React.FC = () => {
  const { checkInCode } = useParams<{ checkInCode: string }>();
  const navigate = useNavigate();
  const { hasRole, isLoading: rolesLoading } = useRoles();
  const [state, setState] = useState<CheckInState>({ phase: "loading" });
  const didSubmit = useRef(false);

  useEffect(() => {
    if (rolesLoading || didSubmit.current) return;

    if (!hasRole("EVENT_CHECKIN") && !hasRole("EVENT_ADMIN")) {
      setState({ phase: "no-permission" });
      return;
    }

    if (!checkInCode) {
      setState({ phase: "error", message: "Invalid check-in link." });
      return;
    }

    didSubmit.current = true;

    checkInByScan(checkInCode).then((result) => {
      if (result.success && result.registration) {
        setState({ phase: "success", registration: result.registration });
      } else if (result.alreadyCheckedIn && result.registration) {
        setState({ phase: "already", registration: result.registration });
      } else {
        setState({
          phase: "error",
          message: result.error ?? "Check-in failed.",
        });
      }
    });
  }, [rolesLoading, hasRole, checkInCode]);

  const formatTime = (iso?: string) =>
    iso
      ? new Date(iso).toLocaleString("de-DE", {
          dateStyle: "short",
          timeStyle: "short",
        })
      : "—";

  if (state.phase === "loading") {
    return (
      <div className="checkin-page">
        <div className="checkin-card">
          <div className="checkin-spinner" aria-label="Processing check-in" />
          <p className="checkin-status-text">Processing check-in…</p>
        </div>
      </div>
    );
  }

  if (state.phase === "no-permission") {
    return (
      <div className="checkin-page">
        <div className="checkin-card checkin-card--info">
          <h1 className="checkin-title">Check-in Link</h1>
          <p className="checkin-body">
            This link is for event staff only. To check in, please show your
            QR code from your registration confirmation to a staff member.
          </p>
          <button className="checkin-btn" onClick={() => navigate("/my-registrations")}>
            My Registrations
          </button>
        </div>
      </div>
    );
  }

  if (state.phase === "success") {
    const reg = state.registration;
    return (
      <div className="checkin-page">
        <div className="checkin-card checkin-card--success">
          <div className="checkin-icon">✅</div>
          <h1 className="checkin-title">Check-in Successful</h1>
          <p className="checkin-name">{reg.userName}</p>
          <p className="checkin-body">{reg.userEmail}</p>
          <p className="checkin-time">Checked in at {formatTime(reg.checkInTime)}</p>
          <button className="checkin-btn" onClick={() => navigate("/cgi-events")}>
            Go to Events
          </button>
        </div>
      </div>
    );
  }

  if (state.phase === "already") {
    const reg = state.registration;
    return (
      <div className="checkin-page">
        <div className="checkin-card checkin-card--warning">
          <div className="checkin-icon">⚠️</div>
          <h1 className="checkin-title">Already Checked In</h1>
          <p className="checkin-name">{reg.userName}</p>
          <p className="checkin-body">{reg.userEmail}</p>
          <p className="checkin-time">
            Previously checked in at {formatTime(reg.checkInTime)}
          </p>
          <button className="checkin-btn" onClick={() => navigate("/cgi-events")}>
            Go to Events
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="checkin-page">
      <div className="checkin-card checkin-card--error">
        <div className="checkin-icon">❌</div>
        <h1 className="checkin-title">Check-in Failed</h1>
        <p className="checkin-body">{state.message}</p>
        <button className="checkin-btn" onClick={() => navigate("/cgi-events")}>
          Go to Events
        </button>
      </div>
    </div>
  );
};

export default CheckInPage;
